import { useTranslation } from "react-i18next";
import { paymentBadgeState, type PaymentOrderLike } from "../../utils/orderPayment";
import { formatDate } from "../../utils/date";

interface PaymentBadgeProps {
  order: PaymentOrderLike & { paid_at?: string | null };
  size?: "sm" | "md";
}

/**
 * Deferred-payment badge (Story 11.6-1). Renders nothing for a plain (non-deferred) order;
 * "Impayé" / "En retard (n j)" for unpaid, "Payé le {date}" for a settled deferred order.
 * Display logic lives in paymentBadgeState so the detail and list pages can't drift.
 */
export function PaymentBadge({ order, size = "md" }: PaymentBadgeProps) {
  const { t } = useTranslation("orders");
  const state = paymentBadgeState(order);
  if (state.kind === "none") return null;

  const sizeCls = size === "sm" ? "px-2 py-0.5 text-xs" : "px-2 py-1 text-sm";
  const colorCls =
    state.kind === "paid"
      ? "bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200"
      : state.kind === "overdue"
        ? "bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200"
        : "bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200";

  const label =
    state.kind === "paid"
      ? t("payment_badge_paid", { date: order.paid_at ? formatDate(order.paid_at) : "" })
      : state.kind === "overdue"
        ? t("payment_badge_overdue", { days: state.daysOverdue })
        : t("payment_badge_unpaid");

  return (
    <span
      data-testid="payment-badge"
      className={`inline-flex items-center rounded font-medium ${sizeCls} ${colorCls}`}
    >
      {label}
    </span>
  );
}
